/**
 * Device Trio — Laptop + Tablet + Phone staggered with brand-tinted screens.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { laptopFrame, tabletFrame, phoneFrame, screenContent } from "./_mockHelpers.js";

export const deviceTrio: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";
  const isVertical = ctx.format === "9:16";

  const laptopW = isVertical ? 720 : 880;
  const tabletW = isVertical ? 320 : 360;
  const tabletH = Math.round(tabletW * 1.36);
  const phoneW = isVertical ? 220 : 240;

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const lapId = `${clipPrefix}-laptop`;
  const tabId = `${clipPrefix}-tablet`;
  const phId = `${clipPrefix}-phone`;
  const glowId = `${clipPrefix}-glow`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const lapInner = screenContent({ media: inputs.media, mediaIndex: 0, accent, fallback: "feed" });
  const tabInner = screenContent({ media: inputs.media, mediaIndex: 1, accent, fallback: "list" });
  const phInner = screenContent({ media: inputs.media, mediaIndex: 2, accent, fallback: "app" });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:32px;padding:64px;">
    ${caption ? `<div id="${capId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(caption)}</div>` : ""}
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;align-items:flex-end;justify-content:center;gap:${isVertical ? 0 : -20}px;margin-top:8px;width:100%;">
      <div id="${glowId}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:${laptopW * 1.2}px;height:${laptopW * 0.8}px;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(accent, 0.4)} 0%, transparent 70%);filter:blur(32px);"></div>
      <div style="position:relative;z-index:1;">
        ${laptopFrame({ id: lapId, width: laptopW, accent, inner: lapInner })}
      </div>
      <div style="position:relative;z-index:2;margin-left:-${laptopW * 0.12}px;margin-bottom:${laptopW * 0.04}px;">
        ${tabletFrame({ id: tabId, width: tabletW, height: tabletH, accent, inner: tabInner })}
      </div>
      <div style="position:relative;z-index:3;margin-left:-${tabletW * 0.18}px;margin-bottom:${tabletW * 0.06}px;">
        ${phoneFrame({ id: phId, width: phoneW, accent, inner: phInner })}
      </div>
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${lapId}`, { opacity: 0, y: 40, scale: 0.95 }),
    gsapSetLine(`#${tabId}`, { opacity: 0, y: 60, x: -30, scale: 0.92 }),
    gsapSetLine(`#${phId}`, { opacity: 0, y: 80, x: -40, scale: 0.88 }),
    gsapSetLine(`#${glowId}`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${glowId}`, { opacity: 0.85, duration: 0.8, ease: "raw:\"power2.out\"" }, 0.0),
    t.to(`#${lapId}`, { opacity: 1, y: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.1),
    t.to(`#${tabId}`, { opacity: 1, y: 0, x: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.3),
    t.to(`#${phId}`, { opacity: 1, y: 0, x: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.5),
    t.to(`#${lapId}`, { y: -4, duration: Math.max(1.2, clipDurationSec - 2.0), ease: "raw:\"sine.inOut\"" }, 1.5),
    t.to(`#${tabId}`, { y: -8, duration: Math.max(1.2, clipDurationSec - 2.0), ease: "raw:\"sine.inOut\"" }, 1.7),
    t.to(`#${phId}`, { y: -12, duration: Math.max(1.2, clipDurationSec - 2.0), ease: "raw:\"sine.inOut\"" }, 1.9),
  ];
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.05));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.2));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
